import { unpackStringToImpactScores } from '@upright/common/model/output/scores';
import { computeNetScore } from '@upright/common/model/output/scores/compute-net-score';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { createContext, useContext } from 'react';
import { ModelEntityWithScores } from '../../../../common/model/output/scores/model-entity-data/types';
import { RenderOptions, RenderViewComponentParams } from '../../../../common/renderer/types';
import { useRenderViewOptions } from '../utils/use-render-view-options';

interface RenderViewContext {
  options: Partial<RenderOptions>;
  componentParams: RenderViewComponentParams[];
  data: ModelEntityWithScores[];
}

function buildData(options: Partial<RenderOptions>): ModelEntityWithScores[] {
  const item = options.items?.[0];
  if (!item || !options.version || !item.renderProfileData) {
    throw Error('Version required');
  }
  const impactScoresByIVV = unpackStringToImpactScores(item.renderProfileData);
  const entity: ModelEntityWithScores = {
    name: item.name,
    id: item.id,
    type: item.type,
    modelResults: [
      {
        startDate: item.startDate,
        endDate: item.endDate,
        productScoreVersion: options.version,
        resultId: item.resultId!,
        version: options.version,
        netScoresByType: {
          [FinalImpactScoreType.RELATIVE]: computeNetScore(impactScoresByIVV, FinalImpactScoreType.RELATIVE),
        },
        impactScoresByTIVV: {
          r: impactScoresByIVV,
        },
      },
    ],
  };
  return [entity];
}

export function useRenderViewContextBuilder(): RenderViewContext {
  const options = useRenderViewOptions();

  const versionToUse = options.version;
  if (!versionToUse) {
    throw Error('Version required');
  }

  const componentParams = options.componentParams || [];

  return {
    options,
    componentParams,
    data: buildData(options),
  };
}

const ctx = createContext({} as RenderViewContext);

export function useRenderViewContext() {
  return useContext(ctx);
}

export const RenderViewContextProvider = ctx.Provider;
